import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUp } from "lucide-react";

/**
 * Global site footer — rendered once from App.js so every route inherits it.
 * Palette matches the industrial header: deep navy surface, slate copy,
 * amber accent for the MD email so the leadership contact stands out.
 *
 * Layout: 4 top-aligned columns on md+ (Brand · Company · Contact · Legal).
 * Subtle vertical dividers between columns. Back-to-Top pill appears once
 * the user scrolls past 70% of the viewport height.
 */
export default function SiteFooter() {
  return (
    <>
      <footer
        className="site-footer mt-12 border-t-4 border-sarthi-primary bg-[#0F1B2D] text-slate-300"
        data-testid="site-footer"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-10 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:gap-6 lg:divide-x lg:divide-white/5">
          {/* Column 1 — Brand */}
          <div className="lg:pr-6" data-testid="footer-col-brand">
            <div className="font-display text-xl font-extrabold text-white">Sarthi Associates</div>
            <div className="mt-1 text-sm text-slate-400">Frontline workforce · Recruitment · Deployment</div>
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              India's trusted platform for welders, fitters, riggers, electricians, technicians &amp; more —
              powering EPC, thermal power &amp; heavy industrial projects nationwide.
            </p>
            <div className="mt-5 flex items-start gap-2 text-sm">
              <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-sarthi-primary" />
              <span>Sarthi Associates, Noida,<br />Uttar Pradesh, India</span>
            </div>
          </div>

          {/* Column 2 — Company */}
          <div className="lg:px-6" data-testid="footer-col-company">
            <div className="mb-4 font-sans text-xs font-extrabold uppercase tracking-[0.15em] text-slate-400">Company</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/about" className="hover:text-white">About Us</Link></li>
              <li><Link to="/executive-teams" className="hover:text-white">Our Team</Link></li>
              <li><Link to="/partners" className="hover:text-white">Business Partner</Link></li>
              <li><Link to="/consultancy" className="hover:text-white">Services</Link></li>
              <li><Link to="/plans" className="font-semibold text-amber-300 hover:text-amber-200">Premium Plans</Link></li>
              <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
            </ul>
          </div>

          {/* Column 3 — Contact */}
          <div className="lg:px-6" data-testid="footer-col-contact">
            <div className="mb-4 font-sans text-xs font-extrabold uppercase tracking-[0.15em] text-slate-400">Contact</div>
            <ul className="space-y-3 text-sm">
              <li>
                <a data-testid="footer-info-email" href="mailto:info@sarthiassociates.com" className="inline-flex items-center gap-2 hover:text-white">
                  <Mail className="h-4 w-4 flex-none text-sarthi-primary" />
                  <span className="truncate">info@sarthiassociates.com</span>
                </a>
                <div className="ml-6 text-xs text-slate-500">General enquiries</div>
              </li>
              <li>
                <a data-testid="footer-contact-email" href="mailto:contact@sarthiassociates.com" className="inline-flex items-center gap-2 hover:text-white">
                  <Mail className="h-4 w-4 flex-none text-sarthi-primary" />
                  <span className="truncate">contact@sarthiassociates.com</span>
                </a>
                <div className="ml-6 text-xs text-slate-500">Support &amp; operations</div>
              </li>
              <li>
                <a data-testid="footer-md-email" href="mailto:cmd@sarthiassociates.com" className="inline-flex items-center gap-2 font-semibold text-amber-300 hover:text-amber-200">
                  <Mail className="h-4 w-4 flex-none" />
                  <span className="truncate">cmd@sarthiassociates.com</span>
                </a>
                <div className="ml-6 text-xs text-amber-200/60">Office of the Managing Director</div>
              </li>
              <li>
                <a href="tel:+919131847732" className="inline-flex items-center gap-2 hover:text-white">
                  <Phone className="h-4 w-4 flex-none text-sarthi-primary" />
                  +91 91318 47732
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4 — Legal */}
          <div className="lg:pl-6" data-testid="footer-col-legal">
            <div className="mb-4 font-sans text-xs font-extrabold uppercase tracking-[0.15em] text-slate-400">Legal</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link data-testid="footer-faq" to="/faq" className="hover:text-white">FAQ</Link></li>
              <li><Link data-testid="footer-privacy" to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
              <li><Link data-testid="footer-terms" to="/terms" className="hover:text-white">Terms of Service</Link></li>
              <li><Link data-testid="footer-disclaimer" to="/disclaimer" className="hover:text-white">Disclaimer</Link></li>
              <li><Link data-testid="footer-cookie-policy" to="/cookie-policy" className="hover:text-white">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-4 text-xs text-slate-500 sm:px-6">
            <span>© {new Date().getFullYear()} Sarthi Associates. All rights reserved.</span>
            <span>Trusted jobs for frontline workers · Made in India</span>
          </div>
        </div>
      </footer>
      <BackToTop />
    </>
  );
}

function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      // Show once the user has scrolled past 70% of viewport height —
      // roughly when the footer area starts to enter the viewport.
      setVisible(window.scrollY > window.innerHeight * 0.7);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      data-testid="back-to-top"
      aria-label="Back to top"
      className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-sarthi-primary text-white shadow-[0_4px_0_0_#B84A25,0_8px_24px_-4px_rgba(0,0,0,0.35)] transition hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_2px_0_0_#B84A25,0_4px_12px_-4px_rgba(0,0,0,0.35)] print:hidden"
    >
      <ArrowUp className="h-5 w-5" strokeWidth={2.5} />
    </button>
  );
}
